import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import './Certificates.css';

const Certificates = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const isAdmin = user?.role === 'admin';
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCertificates = async () => {
      try {
        const endpoint = isAdmin
          ? '/certificates/admin'
          : '/certificates/my-certificates';
        const response = await api.get(endpoint);
        setCertificates(response.data);
      } catch (error) {
        console.error('Error fetching certificates:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCertificates();
  }, [isAdmin]);

  const handleDownload = async (certId) => {
    try {
      const response = await api.get(`/certificates/download/${certId}`, {
        responseType: 'blob',
      });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      const contentDisposition = response.headers['content-disposition'];
      let filename = 'certificate.pdf';
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?([^";\n]+)"?/);
        if (match) filename = match[1];
      }

      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading certificate:', error);
      alert(t('certificates.downloadFailed'));
    }
  };

  if (loading) return <div className="loading">{t('certificates.loading')}</div>;

  const renderLearnerView = () => (
    <>
      <section className="certificates-hero">
        <div className="hero-content">
          <p className="eyebrow">{t('certificates.credentials')}</p>
          <h1>{t('certificates.title')}</h1>
          <p>{t('certificates.subtitle')}</p>
        </div>
        <div className="hero-card">
          <span className="label">{t('certificates.earned')}</span>
          <p className="hero-value">{certificates.length}</p>
          <p className="hero-note">
            {certificates.length > 0
              ? t('certificates.keepStreakGoing')
              : t('certificates.completeFirst')}
          </p>
        </div>
      </section>

      {certificates.length === 0 ? (
        <div className="empty-state">
          <p>{t('certificates.noCerts')}</p>
          <p>{t('certificates.completeToEarn')}</p>
        </div>
      ) : (
        <div className="certificates-grid">
          {certificates.map((cert) => (
            <article key={cert.id} className="certificate-card">
              <header>
                <div className="certificate-icon">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M12 15l-3 3m0 0l-3-3m3 3V9m12 3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <p className="label">{t('certificates.courseLabel')}</p>
                  <h3>{cert.course_title}</h3>
                  <p className="instructor">{cert.instructor_name}</p>
                </div>
                <span className="badge">{t('certificates.verified')}</span>
              </header>
              <div className="certificate-meta">
                <div>
                  <span className="meta-label">{t('certificates.issued')}</span>
                  <p>{new Date(cert.issued_at).toLocaleDateString()}</p>
                </div>
                <div>
                  <span className="meta-label">{t('certificates.certNumber')}</span>
                  <p>{cert.certificate_number}</p>
                </div>
                <div>
                  <span className="meta-label">{t('certificates.verification')}</span>
                  <p>{cert.verification_code}</p>
                </div>
              </div>
              <div className="certificate-actions">
                <button
                  onClick={() => handleDownload(cert.id)}
                  className="btn-primary"
                >
                  {t('certificates.downloadPdf')}
                </button>
              </div>
            </article>
          ))}
        </div>
      )}
    </>
  );

  const renderAdminView = () => (
    <>
      <section className="certificates-hero admin">
        <div className="hero-content">
          <p className="eyebrow">{t('certificates.adminEyebrow')}</p>
          <h1>{t('certificates.adminTitle')}</h1>
          <p>{t('certificates.adminSubtitle')}</p>
        </div>
        <div className="hero-card">
          <span className="label">{t('certificates.totalIssued')}</span>
          <p className="hero-value">{certificates.length}</p>
          <p className="hero-note">{t('certificates.mostRecentFirst')}</p>
        </div>
      </section>

      <div className="admin-certificates-table">
        <div className="table-header">
          <span>{t('certificates.student')}</span>
          <span>{t('certificates.course')}</span>
          <span>{t('certificates.issued')}</span>
          <span>{t('certificates.certNumber')}</span>
          <span>{t('certificates.actions')}</span>
        </div>
        {certificates.length === 0 ? (
          <div className="empty-state">
            <p>{t('certificates.noCertsAdmin')}</p>
          </div>
        ) : (
          certificates.map((cert) => (
            <div key={cert.id} className="table-row">
              <div className="student-cell">
                <strong>{cert.student_name}</strong>
                <span>{cert.student_email}</span>
              </div>
              <div className="course-cell">
                <p className="course-title">{cert.course_title}</p>
                <span>{cert.instructor_name}</span>
              </div>
              <span className="issued-cell">
                {new Date(cert.issued_at).toLocaleDateString()}
              </span>
              <span className="certificate-cell">{cert.certificate_number}</span>
              <div className="actions-cell">
                <button
                  className="btn-secondary"
                  onClick={() => handleDownload(cert.id)}
                >
                  {t('certificates.download')}
                </button>
                <span className="verification-code">{cert.verification_code}</span>
              </div>
            </div>
          ))
        )}
      </div>
    </>
  );

  return (
    <div className={`page certificates-page ${isAdmin ? 'manage-view' : ''}`}>
      <div className="container">
        {isAdmin ? renderAdminView() : renderLearnerView()}
      </div>
    </div>
  );
};

export default Certificates;
